<template>
  <form class="offer-form uk-form-stacked" @submit.prevent="submit">
    <validation-alert-box :errors="fields.errors.base" />

    <!-- Basic Data -->
    <fieldset class="uk-fieldset uk-margin-medium-top">
      <legend class="uk-legend">{{$t('fieldsets.basicFields')}}</legend>

      <product-combobox
        :label="$t('fields.product')"
        v-model="fields.values.product"
        :errors="fields.errors.product"
        :placeholder="$t('placeholders.withoutProduct')"
        :disabled="!isEditable"
      />

      <contract-combobox
        :label="$t('fields.contract')"
        v-model="fields.values.contract"
        :errors="fields.errors.contract"
        :placeholder="$t('placeholders.withoutContract')"
        :disabled="!isEditable"
      />
    </fieldset>

    <!-- Prices -->
    <fieldset class="uk-fieldset uk-margin-medium-top">
      <legend class="uk-legend uk-margin">
        {{$t('fieldsets.prices')}}
      </legend>

      <price-manager
        :value="fields.values.prices"
        @change="fields.change.prices"
        :errors="fields.errors.prices"
        :disabled="!isEditable"
      />
    </fieldset>

    <!-- Publications -->
    <fieldset class="uk-fieldset uk-margin-medium-top">
      <legend class="uk-legend uk-margin">
        {{$t('fieldsets.publications')}}
      </legend>

      <publication-manager
        :value="fields.values.publications"
        @change="fields.change.publications"
        :errors="fields.errors.publications"
        :disabled="!isEditable"
      />
    </fieldset>

    <!-- Details -->
    <fieldset class="uk-fieldset uk-margin-medium-top">
      <legend class="uk-legend">{{$t('fieldsets.details')}}</legend>

      <text-input
        type="number"
        :label="$t('fields.maxOrderableAmount')"
        v-model.number="fields.values.maxOrderableAmount"
        :errors="fields.errors.maxOrderableAmount"
        :disabled="!isEditable"
        :min="1"
        class="uk-width-1-4"
      />

      <text-list-input
        :label="$t('fields.usps')"
        v-model="fields.values.usps"
        :errors="fields.errors.usps"
        :disabled="!isEditable"
      />

      <text-list-input
        :label="$t('fields.summaryTexts')"
        v-model="fields.values.summaryTexts"
        :errors="fields.errors.summaryTexts"
        :disabled="!isEditable"
      />

      <text-area-input
        :label="$t('fields.priceExtraInformation')"
        v-model="fields.values.priceExtraInformation"
        :errors="fields.errors.priceExtraInformation"
        :disabled="!isEditable"
      />
    </fieldset>

    <!-- Media -->
    <fieldset class="uk-fieldset uk-margin-medium-top">
      <legend class="uk-legend uk-margin">
        {{$t('fieldsets.media')}}
      </legend>

      <attachment-manager
        attachment-type="MEDIA_OBJECT"
        v-model="fields.values.media"
        :errors="fields.errors.media"
        :disabled="!isEditable"
      />
    </fieldset>

    <div class="uk-margin-medium-top">
      <submit-button type="primary" :disabled="!isSubmittable" />
      <cancel-button />
    </div>
  </form>
</template>

<script>
import pick from 'lodash/pick';
import AttachmentManager from '@/components/AttachmentManager';
import CancelButton from '@/components/CancelButton';
import CommonButton from '@/components/CommonButton';
import ContractCombobox from '@/components/ContractCombobox';
import Form from '@/mixins/Form';
import PriceManager from '@/components/PriceManager';
import ProductCombobox from '@/components/ProductCombobox';
import PublicationManager from '@/components/PublicationManager';
import SubmitButton from '@/components/SubmitButton';
import TenantCombobox from '@/components/TenantCombobox';
import TextInput from '@/components/TextInput';
import TextListInput from '@/components/TextListInput';
import TextAreaInput from '@/components/TextareaInput';
import ValidationAlertBox from '@/components/ValidationAlertBox';

export default {
  name: 'offer-form',
  mixins: [
    Form({
      contract: { type: Form.Types.Reference },
      maxOrderableAmount: true,
      media: { type: Form.Types.ReferenceList, dumpTo: 'mediaObjectIds' },
      prices: {
        type: Form.Types.Array,
        dump(prices) {
          return (prices || []).map((price) => {
            const newPrice = pick(price, ['type', 'paymentFrequency', 'value']);
            if (price.organization) {
              newPrice.organizationId = price.organization.id;
            } else {
              newPrice.organizationId = null;
            }
            delete newPrice.organization;
            return newPrice;
          });
        },
      },
      product: { type: Form.Types.Reference },
      publications: {
        type: Form.Types.Array,
        dump(publications) {
          return (publications || []).map((publication) => {
            const newPublication = pick(publication, ['list', 'position']);
            if (publication.tenant) {
              newPublication.tenantId = publication.tenant.id;
            } else {
              newPublication.tenantId = null;
            }
            delete newPublication.tenant;
            return newPublication;
          });
        },
      },
      summaryTexts: { type: Form.Types.Array },
      usps: { type: Form.Types.Array },
      priceExtraInformation: true,
    }),
  ],
  components: {
    AttachmentManager,
    CancelButton,
    CommonButton,
    ContractCombobox,
    PriceManager,
    ProductCombobox,
    PublicationManager,
    SubmitButton,
    TenantCombobox,
    TextInput,
    TextAreaInput,
    TextListInput,
    ValidationAlertBox,
  },
  computed: {
    isPopulated() {
      return !!(this.fields.values.contract || this.fields.values.product);
    },
  },
  i18n: {
    messages: {
      de: {
        placeholders: {
          withoutProduct: 'ohne Produkt',
          withoutContract: 'ohne Vertrag',
        },
      },
    },
  },
};
</script>



// WEBPACK FOOTER //
// OfferForm.vue?d57d709c